import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionActions = ({ subscriptionData, onUpdate }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleToggleAutoRenew = async () => {
    setLoading(true);
    try {
      // In production, call API to toggle auto-renewal
      await new Promise(resolve => setTimeout(resolve, 1000));
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling auto-renewal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelReason?.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      // In production, call API to cancel subscription
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowCancelModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSupport = () => {
    window.open('/ai-help-assistant', '_blank');
  };

  const quickActions = [
    {
      id: 'upgrade',
      label: 'Upgrade Plan',
      icon: 'ArrowUp',
      description: 'Get more features and capacity',
      color: 'primary',
      action: () => console.log('Navigate to plans tab')
    },
    {
      id: 'billing',
      label: 'Update Payment',
      icon: 'CreditCard',
      description: 'Change payment method',
      color: 'blue',
      action: () => console.log('Navigate to payment tab')
    },
    {
      id: 'invoice',
      label: 'Download Invoice',
      icon: 'Download',
      description: 'Get latest billing invoice',
      color: 'green',
      action: () => console.log('Download invoice')
    },
    {
      id: 'support',
      label: 'Contact Support',
      icon: 'HelpCircle',
      description: 'Get help with your subscription',
      color: 'purple',
      action: handleContactSupport
    }
  ];

  return (
    <div className="space-y-6">
      {/* Quick Actions Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions?.map((action) => (
            <button
              key={action?.id}
              onClick={action?.action}
              className="flex flex-col items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className={`w-12 h-12 rounded-lg bg-${action?.color}-50 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <Icon name={action?.icon} className={`w-6 h-6 text-${action?.color}-600`} />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900 mb-1">{action?.label}</p>
                <p className="text-xs text-gray-600">{action?.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Subscription Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Subscription Management</h3>
        <div className="space-y-4">
          {/* Auto-Renewal Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="RefreshCw" className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Auto-Renewal</p>
                <p className="text-sm text-gray-600">
                  {subscriptionData?.autoRenew
                    ? 'Your subscription will automatically renew' :'Your subscription will not renew automatically'}
                </p>
              </div>
            </div>
            <Button
              variant={subscriptionData?.autoRenew ? 'outline' : 'default'}
              onClick={handleToggleAutoRenew}
              loading={loading}
            >
              {subscriptionData?.autoRenew ? 'Disable' : 'Enable'}
            </Button>
          </div>

          {/* Early Renewal */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Calendar" className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Renew Early</p>
                <p className="text-sm text-gray-600">
                  Renew your subscription now and get 10% off
                </p>
              </div>
            </div>
            <Button variant="outline">
              Renew Now
            </Button>
          </div>

          {/* Cancel Subscription */}
          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="XCircle" className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900">Cancel Subscription</p>
                <p className="text-sm text-red-700">
                  End your subscription at the end of the billing period
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Cancel Subscription?
                </h3>
                <p className="text-sm text-gray-600">
                  We're sorry to see you go. Your subscription will remain active until{' '}
                  {new Date(subscriptionData?.nextBillingDate)?.toLocaleDateString()}, and you won't be charged again.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Please tell us why you're cancelling (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={4}
                placeholder="Your feedback helps us improve..."
              />
            </div>

            <div className="flex gap-3">
              <Button
                fullWidth
                variant="outline"
                onClick={() => setShowCancelModal(false)}
                disabled={loading}
              >
                Keep Subscription
              </Button>
              <Button
                fullWidth
                variant="danger"
                onClick={handleCancelSubscription}
                loading={loading}
              >
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionActions;